import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { Label } from '@/components/shared/Label/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Selection, toggleAll, toggleSelection } from './selection';

export interface DropdownMultiSelectOption {
  value: string;
  label: string;
  /** Etiqueta à direita da linha, ex.: a moeda da oferta. */
  tag?: string;
}

interface DropdownMultiSelectProps {
  options: DropdownMultiSelectOption[];
  /** Lista vazia = "todos"; `null` = nenhum. */
  values: Selection;
  onValuesChange: (values: Selection) => void;
  /** Texto da linha "todos" e do gatilho sem restrição. */
  allLabel: string;
  /** Texto do gatilho sem nenhum marcado. */
  noneLabel: string;
  /** Resumo do gatilho com 2+ marcados; recebe a quantidade. */
  selectedCountLabel: (count: number) => string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  dataCy?: string;
}

function CheckMark({ visible }: { visible: boolean }) {
  return (
    <Check
      size={14}
      className={cn('shrink-0 text-primary', visible ? 'visible' : 'invisible')}
    />
  );
}

/**
 * Dropdown fechado com lista marcável. "Todos" sai como `[]` — o mesmo que a
 * API entende. A linha "todos" alterna entre marcar e desmarcar tudo; sem
 * nenhuma marca o valor é `null` e quem usa decide como bloquear.
 */
export function DropdownMultiSelect({
  options,
  values,
  onValuesChange,
  allLabel,
  noneLabel,
  selectedCountLabel,
  label,
  helperText,
  disabled,
  dataCy,
}: DropdownMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const allValues = options.map((option) => option.value);
  const isAll = values !== null && values.length === 0;

  const summary = (() => {
    if (values === null) {
      return noneLabel;
    }

    if (isAll) {
      return allLabel;
    }

    if (values.length === 1) {
      return options.find((option) => option.value === values[0])?.label ?? '';
    }

    return selectedCountLabel(values.length);
  })();

  return (
    <div className='w-full flex flex-col gap-2'>
      {label && <Label>{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type='button'
            data-cy={dataCy}
            className={cn(
              'flex h-[40px] w-full items-center justify-between gap-2 rounded-md border bg-[#09090b] px-3 text-sm text-muted disabled:bg-bgDisabled disabled:cursor-not-allowed',
              helperText
                ? 'border-error'
                : isOpen
                  ? 'border-primary'
                  : 'border-colorBorder'
            )}
          >
            <span className='line-clamp-1 text-left'>{summary}</span>
            <ChevronDown
              size={16}
              className={cn(
                'shrink-0 opacity-50 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className='w-[var(--radix-popover-trigger-width)] max-h-64 overflow-y-auto p-1.5'
        >
          <button
            type='button'
            data-cy='dropdown-option-all'
            onClick={() => onValuesChange(toggleAll(values))}
            className='flex w-full items-center justify-between gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-semibold hover:bg-white/5'
          >
            <span className='truncate'>{allLabel}</span>
            <CheckMark visible={isAll} />
          </button>
          <div className='mx-0.5 my-1 border-t border-colorBorder' />
          {options.map((option) => (
            <button
              key={option.value}
              type='button'
              data-cy='dropdown-option'
              onClick={() =>
                onValuesChange(toggleSelection(values, option.value, allValues))
              }
              className='flex w-full items-center justify-between gap-2.5 rounded-md px-2.5 py-2 text-left text-sm hover:bg-white/5'
            >
              <span className='truncate'>{option.label}</span>
              <span className='flex shrink-0 items-center gap-2'>
                {option.tag && (
                  <span className='rounded-full border border-colorBorder px-2 py-0.5 text-[11px] text-zinc-400'>
                    {option.tag}
                  </span>
                )}
                <CheckMark visible={isAll || Boolean(values?.includes(option.value))} />
              </span>
            </button>
          ))}
        </PopoverContent>
      </Popover>
      {helperText && <p className='text-sm text-error'>{helperText}</p>}
    </div>
  );
}
